import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import { ActivateInvitationRequestSchema } from '@jq4race/shared';
import { Button } from '@jq4race/ui';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { env } from '../../../../app/env';
import { useAuthServices } from '../auth-services-context';
import { AuthShell } from '../components/AuthShell';
import { ErrorBanner } from '../components/ErrorBanner';
import { PasswordField } from '../components/PasswordField';

function buildActivateSchema(t: (key: string) => string) {
  return ActivateInvitationRequestSchema.omit({ invitationToken: true })
    .extend({ confirm: z.string() })
    .refine((d) => d.password === d.confirm, {
      message: t('shared.passwordMismatch'),
      path: ['confirm'],
    });
}

type ActivateFormValues = z.infer<ReturnType<typeof buildActivateSchema>>;

export function ActivatePage() {
  const { t } = useTranslation('auth');
  const auth = useAuthServices();
  const { token } = useParams({ from: '/activate/$token' });
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const [role, setRole] = useState<string | undefined>(undefined);
  const navigate = useNavigate();

  const activateSchema = useMemo(() => buildActivateSchema(t), [t]);
  const form = useForm<ActivateFormValues>({
    resolver: zodResolver(activateSchema),
    defaultValues: { password: '', confirm: '' },
    mode: 'onTouched',
  });

  async function onSubmit(values: ActivateFormValues) {
    setError(null);
    try {
      const res = await auth.activateInvitation({
        invitationToken: token,
        password: values.password,
      });
      setRole(res?.role);
      setDone(true);
    } catch (err) {
      setError(err);
    }
  }

  if (done) {
    // Valets (role 'operator') work in the PWA: the admin console login is the
    // wrong destination for them. Falls back to the admin login when
    // VITE_PWA_URL is not configured.
    const goToPwa = role === 'operator' && !!env.pwaUrl;
    return (
      <AuthShell
        title={t('activate.done.title')}
        eyebrow={t('activate.done.eyebrow')}
        description={goToPwa ? t('activate.done.descriptionValet') : t('activate.done.description')}
        aside={{
          illustration: 'envelope',
          eyebrow: t('activate.aside.eyebrow'),
          headline: t('activate.done.aside.headline'),
          body: t('activate.done.aside.body'),
        }}
      >
        {goToPwa ? (
          <Button asChild className="w-full">
            <a href={env.pwaUrl}>{t('activate.done.goToApp')}</a>
          </Button>
        ) : (
          <Button className="w-full" onClick={() => navigate({ to: '/login' })}>
            {t('activate.done.goToSignIn')}
          </Button>
        )}
      </AuthShell>
    );
  }

  const { errors, isSubmitting } = form.formState;

  return (
    <AuthShell
      title={t('activate.title')}
      eyebrow={t('activate.eyebrow')}
      description={t('activate.description')}
      aside={{
        illustration: 'envelope',
        eyebrow: t('activate.aside.eyebrow'),
        headline: t('activate.aside.headline'),
        body: t('activate.aside.body'),
      }}
    >
      <form className="grid gap-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <ErrorBanner error={error} />
        <Controller
          control={form.control}
          name="password"
          render={({ field }) => (
            <PasswordField
              id="password"
              label={t('activate.passwordLabel')}
              value={field.value}
              onChange={field.onChange}
              showCriteria
              autoComplete="new-password"
              required
            />
          )}
        />
        <Controller
          control={form.control}
          name="confirm"
          render={({ field }) => (
            <PasswordField
              id="confirm"
              label={t('shared.confirmPasswordLabel')}
              value={field.value}
              onChange={field.onChange}
              autoComplete="new-password"
              required
            />
          )}
        />
        {errors.confirm ? (
          <p className="text-[13px] text-jq-danger">{errors.confirm.message}</p>
        ) : null}
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? t('activate.submitting') : t('activate.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
